import * as React from 'react'

import * as shadow from '@owl-nest/shadow'

import type { UluleStats } from '../_shared/ululeStats'
import { EMPTY_COLLECTIONS, type Collections } from './collections'
import { useUluleStats } from '../_shared/useUluleStats'
import { PopularCollections, TopCollections } from './sections/Boards'
import { Categories } from './sections/Categories'
import { Cta } from './sections/Cta'
import { DataDigger } from './sections/DataDigger'
import { Hero } from './sections/Hero'
import { Money } from './sections/Money'
import { Status } from './sections/Status'
import { Success } from './sections/Success'

/**
 * Statistiques page main content — the bold redesign of the Django /stats/ page.
 *
 * Both server-rendered (prerender) and hydrated on the client, so it must stay pure: no
 * `window`/`document`/`localStorage`/`matchMedia` reads at render time and no per-request values.
 * The one browser value the tree does read — the Top 10 filter's `?cat=` — goes through
 * `useSyncExternalStore`, whose server snapshot is what the prerender and the hydration render
 * see; anything else reaching for `window` while rendering would break the prerender.
 * The site header/footer are NOT part of this tree — they are mounted separately (see entry-client).
 */
export function App({
  initialStats = null,
  collections = EMPTY_COLLECTIONS,
}: {
  initialStats?: UluleStats | null
  collections?: Collections
}): React.ReactElement {
  // Resolved once here: four sections read the figures, and the hook refetches on mount, so
  // calling it in each would fire the request four times. `collections` needs no hook — it is
  // prerendered only, so the value handed in is the value for the life of the page.
  const stats = useUluleStats(initialStats)

  return (
    <shadow.plausible.TrackingProvider context="Stats">
      <main>
        <Hero stats={stats} />
        <Success stats={stats} />
        <Money stats={stats} topAllTime={collections.topAllTime} />
        <Categories stats={stats} />
        <Status />
        <TopCollections topAllTime={collections.topAllTime} byCategory={collections.topByCategory} />
        <PopularCollections collections={collections.popularNow} />
        <DataDigger />
        <Cta />
      </main>
    </shadow.plausible.TrackingProvider>
  )
}
